import { generateFixedColorGradient } from '@/colors';
import { IThemeContext } from '../../interfaces/types';
import { IAgreement } from '../../interfaces/types';

const NATIVE_HOSTS = /(partiu\.com\.br|ebooks\.app\.br)/i;
const NATIVE_PATH_KEYWORDS = ['biblioteca', 'revista', 'audiolivro', 'audiobook'];

export function isNativeBookstoreUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;

  try {
    const parsed = new URL(url);
    if (!NATIVE_HOSTS.test(parsed.hostname)) return false;

    if (parsed.hostname.endsWith('ebooks.app.br')) return true;

    const lower = url.toLowerCase();
    return NATIVE_PATH_KEYWORDS.some((kw) => lower.includes(kw));
  } catch {
    return false;
  }
}

export function buildNativeIframeUrl(baseUrl: string): string {
  const token = localStorage.getItem('token') || '';
  const tokenExpirationDate = localStorage.getItem('tokenExpirationDate') || '';

  const formattedUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const params = new URLSearchParams({
    isNative: 'true',
    token,
    tokenExpirationDate,
  });

  return `${formattedUrl}/biblioteca?${params.toString()}`;
}

enum BookstoreType {
  BOOK,
  MAGAZINE,
  AUDIOBOOK,
  MULTIPLE,
}

export function mixArrays<T>(...arrays: T[][]): T[][] {
  const mixedArr: T[][] = [];
  const maxSize = Math.max(...arrays.map((a) => a.length));

  for (let i = 0; i <= maxSize; i++) {
    arrays.forEach((arr: T[]) => {
      const el: T[] = arr.pop() as T[];

      if (el) mixedArr.push(el);
    });
  }
  return mixedArr;
}

export interface BookstoreLabels {
  singular: string;
  plural: string;
  prefixedSingular: string;
  prefixedPlural: string;
  prefix: string;
  prefixPlural: string;
}

function getBookstoreType(bookstoreTypes: string[]): BookstoreType {
  if (bookstoreTypes.length > 1) return BookstoreType.MULTIPLE;

  if (bookstoreTypes[0] === 'book') return BookstoreType.BOOK;

  if (bookstoreTypes[0] === 'audiobook') return BookstoreType.AUDIOBOOK;

  return BookstoreType.MAGAZINE;
}

function getBookstoreLabels(bookstoreType: BookstoreType): BookstoreLabels {
  let labels: BookstoreLabels;

  if (bookstoreType === BookstoreType.BOOK)
    labels = {
      singular: 'Livro',
      plural: 'Livros',
      prefix: 'o',
      prefixPlural: 'os',
      prefixedSingular: 'o Livro',
      prefixedPlural: 'os Livros',
    };
  else if (bookstoreType === BookstoreType.MAGAZINE)
    labels = {
      singular: 'Revista',
      plural: 'Revistas',
      prefix: 'a',
      prefixPlural: 'as',
      prefixedSingular: 'a Revista',
      prefixedPlural: 'as Revistas',
    };
  else if (bookstoreType === BookstoreType.AUDIOBOOK)
    labels = {
      singular: 'Audiolivro',
      plural: 'Audiolivros',
      prefix: 'o',
      prefixPlural: 'os',
      prefixedSingular: 'o Audiolivro',
      prefixedPlural: 'os Audiolivros',
    };
  else
    labels = {
      singular: 'Acervo',
      plural: 'Acervo',
      prefix: 'o',
      prefixPlural: 'os',
      prefixedSingular: 'o acervo',
      prefixedPlural: 'os acervo',
    };

  labels.prefixPlural = labels.prefix + 's';
  labels.prefixedPlural = labels.prefixPlural + ' ' + labels.plural;
  labels.prefixedSingular = labels.prefix + ' ' + labels.singular;

  return labels;
}

export function parseAgreementToBookstore(agreement: IAgreement): IThemeContext {
  const bookstoreType = getBookstoreType(agreement.bookstoreTypes);
  const bookstoreLabels = getBookstoreLabels(bookstoreType);

  return {
    ...agreement,
    colors: {
      primaryColor: agreement.primaryColor,
      secondaryColor: agreement.secondaryColor,
      menuColor: agreement.menuColor,
      menuFontColor: agreement.menuFontColor,
      primaryFontColor: agreement.primaryFontColor,
      secondaryFontColor: agreement.secondaryFontColor,
      graphColors: mixArrays(
        generateFixedColorGradient(agreement.primaryColor, 30),
        generateFixedColorGradient(agreement.secondaryColor, 20)
      ),
    },
    bookstoreLabels: bookstoreLabels,
    digitalMagazinesModule: agreement.digitalMagazinesModule,
    isNative: false,
    recurringBook: true,
    telephone: agreement.telephone || '',
    isSetup: false,
    privacyPolicy: '',
    legalText: '',
    openModalLogin: () => { },
    isMenuOpen: false,
    isApp: false,
    isIOS: false,
    fetchBenefits: () => { },
    setLoading: () => { },
    setMenuOpen: () => { },
    updateTheme: () => { },
    resetTheme: () => { },
    getValidSubscriptions: () => [],
    benefits: [],
  };
}

export function parseAgreementToTheme(agreement: IAgreement): IThemeContext {
  return {
    id: agreement.id,
    referralImage: agreement.referralImage,
    referralOffer: agreement.referralOffer,
    referralValue: agreement.referralValue,
    name: agreement.product?.metadata?.name?.value || agreement.name,
    slug: agreement.product?.metadata?.slug?.value || agreement.slug,
    logo: agreement.product?.metadata?.logo?.value || agreement.logo,
    email: agreement.product?.metadata?.email?.value || agreement.email,
    telephone: agreement.product?.metadata?.telephone?.value || agreement.telephone || '',
    idp: agreement.idp,
    integratedLogin: agreement.integratedLogin,
    integrationId: agreement.integrationId,
    banners: agreement.banners || [],
    metadata: agreement.metadata || {},
    hasIntegratedPurchaseOnMobileStore: agreement.hasIntegratedPurchaseOnMobileStore,
    allowCpfLoginMethod: agreement.allowCpfLoginMethod,
    allowPromocodeLoginMethod: agreement.allowPromocodeLoginMethod,
    colors: {
      primaryColor: agreement.product?.metadata?.primarycolor?.value || agreement.primaryColor,
      secondaryColor: agreement.product?.metadata?.secondarycolor?.value || agreement.secondaryColor,
      primaryFontColor: agreement.product?.metadata?.primaryfontcolor?.value || agreement.primaryFontColor,
      secondaryFontColor: agreement.product?.metadata?.secondaryfontcolor?.value || agreement.secondaryFontColor,
      menuColor: agreement.menuColor,
      menuFontColor: agreement.menuFontColor,
      graphColors: mixArrays(
        generateFixedColorGradient(agreement.product?.metadata?.primarycolor?.value || agreement.primaryColor, 30),
        generateFixedColorGradient(agreement.product?.metadata?.secondarycolor?.value || agreement.secondaryColor, 20)
      ),
    },
    customerCentralModule: typeof agreement.product?.metadata?.customercentralmodule?.value === 'string'
      ? agreement.product.metadata.customercentralmodule.value === 'true'
      : agreement.customerCentralModule,
    digitalMagazinesModule: agreement.product?.metadata?.digitalmagazinesmodule?.value ?? agreement.digitalMagazinesModule,
    cpfModule: typeof agreement.product?.metadata?.cpfmodule?.value === 'string'
      ? agreement.product.metadata.cpfmodule.value === 'true'
      : agreement.cpfModule,
    promocodeModule: typeof agreement.product?.metadata?.promocodemodule?.value === 'string'
      ? agreement.product.metadata.promocodemodule.value === 'true'
      : agreement.promocodeModule,
    localModule: typeof agreement.product?.metadata?.localmodule?.value === 'string'
      ? agreement.product.metadata.localmodule.value === 'true'
      : agreement.localModule,
    exClientModule: typeof agreement.product?.metadata?.exclientmodule?.value === 'string'
      ? agreement.product.metadata.exclientmodule.value === 'true'
      : agreement.exClientModule,
    ecommerceModule: typeof agreement.product?.metadata?.ecommercemodule?.value === 'string'
      ? agreement.product.metadata.ecommercemodule.value === 'true'
      : agreement.ecommerceModule,
    bePartnerModule: typeof agreement.product?.metadata?.bepartnermodule?.value === 'string'
      ? agreement.product.metadata.bepartnermodule.value === 'true'
      : agreement.bePartnerModule,
    referralModule: agreement.referralModule,
    disableLibraryModule: agreement.product?.metadata?.disablelibrarymodule?.value === 'true',
    planSaleModule: typeof agreement.product?.metadata?.plansalemodulo?.value === 'string'
      ? agreement.product.metadata.plansalemodulo.value === 'true'
      : agreement.planSaleModule,
    subscriptionModule: typeof agreement.product?.metadata?.subscriptionmodule?.value === 'string'
      ? agreement.product.metadata.subscriptionmodule.value === 'true'
      : agreement.subscriptionModule,
    moduleExternalCode: typeof agreement.product?.metadata?.moduleexternalcode?.value === 'string'
      ? agreement.product.metadata.moduleexternalcode.value === 'true'
      : !!agreement.product?.metadata?.moduleexternalcode?.value,
    isNative: false,
    recurringBook: true,
    cellphone: agreement.cellphone || '',
    contactLink: agreement.contactLink || '',
    chatWidgetScript: agreement.chatWidgetScript,
    isSetup: false,
    isIOS: false,
    productData: {
      isProduct: false,
      isMagazine: false,
      isBookstore: false,
      hasAudioBook: false,
    },
    privacyPolicy: '',
    legalText: '',
    openModalLogin: () => { },
    isMenuOpen: false,
    isApp: false,
    isLoadingBenefits: false,

    fetchBenefits: () => { },
    setLoading: () => { },
    setMenuOpen: () => { },
    updateTheme: () => { },
    resetTheme: () => { },
    getValidSubscriptions: () => [],
    benefits: [],
    bookstoreLabels: null,
    active: agreement.active,
    //product
    isProductSuperApp: !!agreement.product,
    product: agreement.product,
    fetchProductsBenefits: () => { },
    productBenefits: [],
  };
}
